/**
 * Gone quiet (§8.5), self-contained the way `AreasCard` is — its own fetch of
 * `/api/slipping`, its own areas/projects for the drawer, its own "seen this session" memory.
 *
 * `GoneQuiet` itself stays presentational; this just feeds it the same shape
 * `build_briefing`'s `slipping_summary()` used to hand down, computed the same way: the
 * `/api/slipping` list is already ordered stalest-first (load-bearing for §9.2's auto-pick,
 * per that route's own docstring), so truncating to `PREVIEW` client-side reproduces the
 * server-side preview exactly — no new endpoint needed. The cap stays, because the reasoning
 * that put it there is about the *briefing*, not the fetch: showing everything that has ever
 * gone quiet would turn this card back into the exhaustive list §8.2 says the briefing must
 * not be, and it is still a card that lives there.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type Slipping, type SlippingEntry } from "../api";
import { useAreasAndProjects } from "../areasProjects";
import { basicTaskActions } from "../taskActions";
import { useFireworks } from "./fireworksContext";
import { todayLocalDate } from "./recurrence";
import { GoneQuiet } from "./GoneQuiet";

/** Mirrors `services/briefing.py`'s `SLIPPING_PREVIEW` — same reasoning, same number. */
const PREVIEW = 5;

function keepSeen(held: SlippingEntry[], current: SlippingEntry[]): SlippingEntry[] {
  const ids = new Set(current.map((e) => e.id));
  return [...current, ...held.filter((e) => !ids.has(e.id))];
}

export function GoneQuietCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed a task, so the quiet list stays current. */
  refreshKey?: number;
  /** Called after a drawer here changes anything, so siblings relying on the same tasks
   * (Wins, the open task list, wherever they live) know to refetch too. */
  onChange?: () => void | Promise<void>;
}) {
  const { areas: allAreas, projects: allProjects } = useAreasAndProjects(refreshKey);
  const [slipping, setSlipping] = useState<Slipping | null>(null);
  const [error, setError] = useState<string | null>(null);
  const burst = useFireworks();
  const actions = basicTaskActions(burst);
  const today = todayLocalDate();

  const load = useCallback(async () => {
    try {
      setSlipping(await api.slipping());
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const previewAreas = slipping?.areas.slice(0, PREVIEW) ?? [];
  const previewProjects = slipping?.projects.slice(0, PREVIEW) ?? [];

  /**
   * Every quiet area/project seen since this card mounted, so acting on one (which touches
   * it, ending its quiet streak) does not delete the row you acted from — it stays, marked
   * "picked up", until the card itself remounts.
   */
  const [seen, setSeen] = useState<{ areas: SlippingEntry[]; projects: SlippingEntry[] }>({
    areas: [],
    projects: [],
  });

  useEffect(() => {
    if (!slipping) return;
    setSeen((held) => ({
      areas: keepSeen(held.areas, previewAreas),
      projects: keepSeen(held.projects, previewProjects),
    }));
    // `previewAreas`/`previewProjects` are derived fresh from `slipping` every render, not
    // stable references — depending on them would re-run this on every render instead of
    // only when the server data actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slipping]);

  const quietIds = new Set([...previewAreas, ...previewProjects].map((e) => e.id));
  const seenAreas = seen.areas.length ? seen.areas : previewAreas;
  const seenProjects = seen.projects.length ? seen.projects : previewProjects;

  const notifyDrawerChange = useCallback(async () => {
    await load();
    await onChange?.();
  }, [load, onChange]);

  return (
    <>
      {error && (
        <p className="error" style={{ margin: "0 0 0.6rem" }}>
          {error}
        </p>
      )}
      <GoneQuiet
        areas={seenAreas}
        projects={seenProjects}
        stillQuiet={(id) => quietIds.has(id)}
        refreshKey={refreshKey}
        onChanged={notifyDrawerChange}
        today={today}
        allAreas={allAreas}
        allProjects={allProjects}
        actions={actions}
      />
    </>
  );
}
